import {StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {Caption} from "@khanacademy/wonder-blocks-typography";
import {useQuery} from "@repo/data-gql";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {doNotTranslate} from "@repo/i18n";
import {logUnreachableCode} from "@repo/logging";

import type {
    getKhanLabsSettings_user_children as Child,
    getKhanLabsSettings as Data,
    getKhanLabsSettingsVariables as Variables,
} from "./__graphql-types__/getKhanLabsSettings.ts";
import AIGuideSettings, {type AIGuideUser} from "./ai-guide-settings.tsx";

function mapChildToAIGuideUser(child: Child): AIGuideUser {
    return {
        kaid: child?.id ?? "", // this should never be null
        nickname: child?.nickname ?? "", // this should never be null
        isAIGuideEnabled: child.isAIGuideEnabled,
        aiGuideEnrollmentGroup: child.aiGuideEnrollmentGroup,
        aiGuideDisabledReason: child.aiGuideDisabledReason,
        includesDistrictOwnedData: child.includesDistrictOwnedData,
        accessPlans: child.aiGuideAccessPlans,
    };
}

const DATA_QUERY: GqlOperation<Data, Variables> = gqlOp(gql`
    query getKhanLabsSettings($targetKaid: String!) {
        user(kaid: $targetKaid) {
            id
            age
            isParent
            isTeacher
            isAIGuideEnabled
            aiGuideEnrollmentGroup
            aiGuideDisabledReason
            nickname
            includesDistrictOwnedData
            children {
                id
                age
                nickname
                isAIGuideEnabled
                aiGuideEnrollmentGroup
                aiGuideDisabledReason
                includesDistrictOwnedData
                aiGuideAccessPlans {
                    id
                    isActive
                    enrollmentGroup
                }
            }
            hasKhanmigoViaStripe
            everHadKhanmigoViaStripe
            aiGuideAccessPlans {
                id
                isActive
                enrollmentGroup
            }
        }
    }
`);

type Props = {
    targetKaid: string;
};

export default function KhanLabsSettings({
    targetKaid,
}: Props): React.ReactElement {
    const [result] = useQuery(DATA_QUERY, {
        variables: {targetKaid},
        fetchPolicy: FetchPolicy.CacheAndNetwork,
    });

    const ErrorState = <Caption>{doNotTranslate`Couldn't load user.`}</Caption>;
    switch (result.status) {
        case "success":
            const user = result.data.user;
            if (user) {
                if (user.age && user.age < 18) {
                    return (
                        <View>
                            <Caption>
                                {doNotTranslate`Khan Labs is only available to users over the age of 18. If you are enrolled through your parent, ask them to manage your settings for you.`}
                            </Caption>
                        </View>
                    );
                }
                // We only want to exclude children who we know are over 18.
                // This means we will show children if (1) we do not know their
                // age OR (2) we know they are under 18.
                const managedChildren = (user?.children ?? [])
                    .filter((ch) => !ch?.age || ch.age < 18)
                    .map(mapChildToAIGuideUser);
                return (
                    <View>
                        <AIGuideSettings
                            isUnder18={!!user?.age && user?.age < 18}
                            isParent={!!user?.isParent}
                            childrenMaybeUnder18={managedChildren}
                            primaryUser={{
                                kaid: targetKaid,
                                isAIGuideEnabled: user.isAIGuideEnabled,
                                nickname: user?.nickname ?? doNotTranslate`you`,
                                aiGuideDisabledReason:
                                    user.aiGuideDisabledReason,
                                includesDistrictOwnedData:
                                    user.includesDistrictOwnedData,
                                aiGuideEnrollmentGroup:
                                    user.aiGuideEnrollmentGroup,
                                accessPlans: user.aiGuideAccessPlans,
                            }}
                            everHadKhanmigoViaStripe={
                                user.everHadKhanmigoViaStripe
                            }
                        />
                    </View>
                );
            }
            return ErrorState;
        case "error":
        case "aborted":
            return ErrorState;
        case "no-data":
        case "loading":
            return <CircularSpinner size="medium" style={styles.spinner} />;
        default:
            // @ts-expect-error - TS2345 - Argument of type 'any' is not assignable to parameter of type 'never'. | TS2339 - Property 'status' does not exist on type 'never'.
            throw logUnreachableCode("Unknown progress state", result.status);
    }
}

const styles = StyleSheet.create({
    spinner: {
        alignItems: "center",
    },
});

export const DATA_QUERY_FOR_TEST = DATA_QUERY;
